import {
  buildProcessedFile,
  canvasToBlob,
  loadImageFromFile,
  resolveOutputMime,
} from './cover-image-process.js';

export const IMAGE_CROP_PRESETS = {
  square: { ratio: 1, shape: 'rect' },
  wide: { ratio: 16 / 9, shape: 'rect' },
  circle: { ratio: 1, shape: 'circle' },
};

export const IMAGE_CROP_MAX_ZOOM = 4;

export function resolveCropPreset(name) {
  return IMAGE_CROP_PRESETS[name] || IMAGE_CROP_PRESETS.square;
}

export function computeViewportSize(ratio, maxSize) {
  const safeRatio = Number(ratio) > 0 ? Number(ratio) : 1;
  const cap = Math.max(1, Number(maxSize) || 1);
  if (safeRatio >= 1) {
    return {
      width: cap,
      height: Math.max(1, Math.round(cap / safeRatio)),
    };
  }
  return {
    width: Math.max(1, Math.round(cap * safeRatio)),
    height: cap,
  };
}

export function computeCoverScale(imageWidth, imageHeight, viewportWidth, viewportHeight) {
  const nw = Number(imageWidth) || 0;
  const nh = Number(imageHeight) || 0;
  const vw = Number(viewportWidth) || 0;
  const vh = Number(viewportHeight) || 0;
  if (nw <= 0 || nh <= 0 || vw <= 0 || vh <= 0) return 1;
  return Math.max(vw / nw, vh / nh);
}

export function clampCropOffset(
  x,
  y,
  scale,
  imageWidth,
  imageHeight,
  viewportWidth,
  viewportHeight
) {
  const displayW = imageWidth * scale;
  const displayH = imageHeight * scale;
  let nextX = x;
  let nextY = y;
  if (displayW <= viewportWidth) {
    nextX = (viewportWidth - displayW) / 2;
  } else {
    nextX = Math.min(0, Math.max(viewportWidth - displayW, x));
  }
  if (displayH <= viewportHeight) {
    nextY = (viewportHeight - displayH) / 2;
  } else {
    nextY = Math.min(0, Math.max(viewportHeight - displayH, y));
  }
  return { x: nextX, y: nextY };
}

export function createInitialCropTransform(imageWidth, imageHeight, viewportWidth, viewportHeight) {
  const scale = computeCoverScale(imageWidth, imageHeight, viewportWidth, viewportHeight);
  const offset = clampCropOffset(
    (viewportWidth - imageWidth * scale) / 2,
    (viewportHeight - imageHeight * scale) / 2,
    scale,
    imageWidth,
    imageHeight,
    viewportWidth,
    viewportHeight
  );
  return { ...offset, scale };
}

export function applyCropZoom(nextScale, current, originX, originY, minScale, maxScale) {
  const scale = Math.min(maxScale, Math.max(minScale, nextScale));
  if (!(current.scale > 0)) {
    return { x: current.x, y: current.y, scale };
  }
  const factor = scale / current.scale;
  return {
    x: originX - (originX - current.x) * factor,
    y: originY - (originY - current.y) * factor,
    scale,
  };
}

export function resolveCropSourceRect({
  offsetX,
  offsetY,
  scale,
  viewportWidth,
  viewportHeight,
  imageWidth,
  imageHeight,
}) {
  if (!(scale > 0)) {
    return { sx: 0, sy: 0, sw: imageWidth, sh: imageHeight };
  }
  const sx = Math.min(imageWidth, Math.max(0, -offsetX / scale));
  const sy = Math.min(imageHeight, Math.max(0, -offsetY / scale));
  const sw = Math.min(imageWidth - sx, viewportWidth / scale);
  const sh = Math.min(imageHeight - sy, viewportHeight / scale);
  return { sx, sy, sw, sh };
}

export async function cropImageToFile(file, transform, options = {}) {
  const img = await loadImageFromFile(file);
  const imageWidth = img.naturalWidth || img.width;
  const imageHeight = img.naturalHeight || img.height;
  if (!imageWidth || !imageHeight) {
    throw new Error('IMAGE_LOAD_FAILED');
  }

  const viewportWidth = Number(transform.viewportWidth) || 0;
  const viewportHeight = Number(transform.viewportHeight) || 0;
  const scale = Number(transform.scale) || 0;
  if (!viewportWidth || !viewportHeight || !scale) {
    throw new Error('IMAGE_COMPRESS_FAILED');
  }

  const { sx, sy, sw, sh } = resolveCropSourceRect({
    offsetX: transform.x,
    offsetY: transform.y,
    scale,
    viewportWidth,
    viewportHeight,
    imageWidth,
    imageHeight,
  });
  if (!(sw > 0) || !(sh > 0)) {
    throw new Error('IMAGE_COMPRESS_FAILED');
  }

  const ratio = viewportWidth / viewportHeight;
  const output = computeViewportSize(ratio, options.outputMaxDimension || 400);
  const canvas = document.createElement('canvas');
  canvas.width = output.width;
  canvas.height = output.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('IMAGE_COMPRESS_FAILED');
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, output.width, output.height);

  const mime = resolveOutputMime(file.type);
  const quality = mime === 'image/jpeg' ? 0.85 : undefined;
  const blob = await canvasToBlob(canvas, mime, quality);
  return buildProcessedFile(blob, file, mime);
}
